import { useEffect, useState } from 'react';
import { Dialog } from '@/shared/components/ui/Dialog';
import { Icon } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';
import { foldersApi } from '@/shared/folders';
import type { FoldersNode } from '@/shared/folders';

interface Props {
  node: FoldersNode;
  orgId: string;
  error?: string | null;
  onSubmit: (parentId: string | null) => void;
  onClose: () => void;
}

/**
 * Pick a destination folders.
 *
 * A flat list rather than a tree: the tree view is the part of Folders this
 * product deliberately does not copy, and a folder set small enough to browse
 * as cards is small enough to choose from a list.
 *
 * Descendants of the node being moved are filtered out here as a courtesy — the
 * database refuses the cycle either way, and offering a destination that will
 * be rejected is worse than not offering it.
 */
export function MoveDialog({ node, orgId, error, onSubmit, onClose }: Props) {
  const { t } = useI18n();
  const [folders, setFolders] = useState<FoldersNode[] | null>(null);
  const [selected, setSelected] = useState<string | null>(node.parentId);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        setFolders(await foldersApi.listFolders(orgId, controller.signal));
      } catch {
        if (!controller.signal.aborted) setFolders([]);
      }
    })();
    return () => controller.abort();
  }, [orgId]);

  const forbidden = new Set<string>();
  if (folders && node.kind === 'folder') {
    forbidden.add(node.id);
    // Walk down from the node: anything under it would become its own ancestor.
    let added = true;
    while (added) {
      added = false;
      for (const folder of folders) {
        if (folder.parentId && forbidden.has(folder.parentId) && !forbidden.has(folder.id)) {
          forbidden.add(folder.id);
          added = true;
        }
      }
    }
  }

  const options = (folders ?? []).filter((folder) => !forbidden.has(folder.id));

  return (
    <Dialog
      title={t('folders.moveDialog.title', { name: node.name })}
      description={t('folders.moveDialog.description')}
      error={error ?? null}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="tap-target rounded-full border border-border-strong px-4 text-sm text-fg"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onSubmit(selected)}
            disabled={selected === node.parentId}
            className="tap-target rounded-full bg-accent px-4 text-sm font-medium text-accent-fg disabled:opacity-50"
          >
            {t('folders.moveDialog.submit')}
          </button>
        </>
      }
    >
      {folders === null ? (
        <p className="text-sm text-muted">{t('common.loading')}</p>
      ) : (
        <ul className="max-h-72 overflow-y-auto rounded-xl border border-border">
          <li>
            <button
              type="button"
              onClick={() => setSelected(null)}
              aria-pressed={selected === null}
              className={[
                'tap-target flex w-full items-center gap-3 px-3 text-left text-sm',
                selected === null ? 'bg-accent-soft text-accent' : 'text-fg hover:bg-surface-3',
              ].join(' ')}
            >
              <Icon name="folder" className="h-4 w-4 shrink-0" />
              <span className="truncate">{t('folders.root')}</span>
            </button>
          </li>
          {options.map((folder) => (
            <li key={folder.id} className="border-t border-border">
              <button
                type="button"
                onClick={() => setSelected(folder.id)}
                aria-pressed={selected === folder.id}
                className={[
                  'tap-target flex w-full items-center gap-3 px-3 text-left text-sm',
                  selected === folder.id
                    ? 'bg-accent-soft text-accent'
                    : 'text-fg hover:bg-surface-3',
                ].join(' ')}
              >
                <Icon name="folder" className="h-4 w-4 shrink-0" />
                <span className="truncate">{folder.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}





